import {
  adoptionPatch, isDeliveryProfileField, isPersonProfileField, profileFieldValueSchemas, profileRecordValue, sameProfileValue,
  type PendingReview, type Person, type ProfileField, type ProfileFact, type UpdatePerson,
} from '@vigeo/shared';
import { formatPhone } from '@/features/roster';

/**
 * What the candidate typed or sent, as the profile words it (ADR-013, #212). Plain functions over the facts the server
 * derived on read: nothing is stored, and nothing here decides anything. An unreviewed value only informs.
 *
 * THE PERSON RECORD STAYS THE ONE SOURCE every other screen reads (the Overview, the quick look, the Edit forms, the Roster).
 * So the Personal tab shows what the record holds, and a candidate answer that is not on it is shown BESIDE it as an offer:
 * one HR has not reviewed (pending), or one that was reviewed and wins but that HR has not yet used. Either goes onto the
 * record only by "Use the candidate's answer", an ordinary audited update.
 */

export type BadgeTone = 'attention' | 'warning' | 'neutral';
export type OfferReview = PendingReview | 'reviewed';

/** The badge for each stage an offer can be at. ADR-013 §1's table, in HR's words, and the reviewed answer that is not on the record. */
export const reviewBadges: Readonly<Record<OfferReview, { readonly label: string; readonly tone: BadgeTone }>> = {
  'in-progress': { label: 'Not reviewed', tone: 'attention' },
  sent: { label: 'Sent, not reviewed', tone: 'attention' },
  returned: { label: 'Returned', tone: 'warning' },
  approved: { label: 'Reviewed, not in use', tone: 'neutral' },
  reviewed: { label: 'Reviewed, not on the record', tone: 'neutral' },
};

/** A candidate answer that is not on the person record, and where it stands. */
export interface Offer {
  readonly value: unknown;
  readonly review: OfferReview;
  /** When it was saved (pending) or reviewed (reviewed). */
  readonly at: string;
}

export const factFor = (answers: readonly ProfileFact[], field: ProfileField): ProfileFact | undefined =>
  answers.find((fact) => fact.field === field);

/**
 * Every candidate answer for this field that is not on the record, reviewed one first. A reviewed answer that wins is an offer
 * only when the record does not already hold it; the pending one is whatever the server found newer and different.
 */
export function offersFor(person: Person, fact: ProfileFact | undefined): readonly Offer[] {
  if (fact === undefined) return [];
  const offers: Offer[] = [];
  if (fact.effective?.source === 'candidate' && isPersonProfileField(fact.field) && !sameProfileValue(profileRecordValue(person, fact.field), fact.effective.value)) {
    offers.push({ value: fact.effective.value, review: 'reviewed', at: fact.effective.at });
  }
  if (fact.pending !== undefined) offers.push({ value: fact.pending.value, review: fact.pending.review, at: fact.pending.savedAt });
  return offers;
}

/** The sentence under an offer. "Candidate sent a change, not reviewed" is ADR-013's own, for when HR already has a value. */
export function offerNote(fact: ProfileFact, offer: Offer): string {
  switch (offer.review) {
    case 'reviewed': return 'Reviewed, and it is the candidate’s answer that wins. It is not on the record yet: the Overview, the Roster and Edit show the record until you use it.';
    case 'returned': return 'Returned to the candidate, who is changing it. Not reviewed.';
    case 'approved': return isDeliveryProfileField(fact.field)
      ? 'Reviewed, but a review never changes where messages go. Use it to make it the one we send to.'
      : 'Reviewed.';
    case 'in-progress':
    case 'sent':
      return fact.effective === undefined ? 'The candidate’s answer. Not reviewed.' : 'Candidate sent a change, not reviewed.';
  }
}

/** Whether HR may make an offer the record's own: it must be a field the record has, since history and the like have no place to write. */
export const canUseOffer = (field: ProfileField): boolean => isPersonProfileField(field);

/** The one-field patch that makes the offered value the record's, or null where it cannot be (never throws). */
export function patchForOffer(field: ProfileField, offer: Offer): UpdatePerson | null {
  return adoptionPatch(field, offer.value);
}

const present = (...parts: ReadonlyArray<string | null | undefined | false>): string[] => parts.map((part) => (part || '').trim()).filter((part) => part !== '');
const range = (from?: string | null, to?: string | null): string => (from || to ? `${from ?? '?'} to ${to ?? 'now'}` : '');
const joined = (...parts: ReadonlyArray<string | null | undefined | false>): string => present(...parts).join(' · ');
const schemas = profileFieldValueSchemas;

/**
 * A value as lines of words: one line for a text answer, one per entry for a list. The value is parsed against the
 * closed vocabulary's own schema first, so a shape nothing here knows draws nothing rather than something wrong.
 */
export function answerLines(field: ProfileField, value: unknown): readonly string[] {
  switch (field) {
    case 'contact.address': {
      const parsed = schemas['contact.address'].safeParse(value);
      if (!parsed.success) return [];
      const a = parsed.data;
      const line = present(present(a.streetAddress, a.addressLine2).join(', '), a.city, present(a.state, a.postalCode).join(' ')).join(', ');
      return line === '' ? [] : [line];
    }
    case 'contact.emergencyContacts': {
      const parsed = schemas['contact.emergencyContacts'].safeParse(value);
      return parsed.success ? parsed.data.map((c) => joined(`${c.name} (${c.relationship})`, formatPhone(c.phone), c.address)) : [];
    }
    case 'employmentHistory': {
      const parsed = schemas.employmentHistory.safeParse(value);
      return parsed.success
        ? parsed.data.map((e) => joined(present(e.positionHeld, e.facility).join(' at '), range(e.fromDate, e.toDate), e.cityState, e.reasonForLeaving && `left: ${e.reasonForLeaving}`))
        : [];
    }
    case 'references': {
      const parsed = schemas.references.safeParse(value);
      return parsed.success ? parsed.data.map((r) => joined(r.name, r.relationship, r.organization, formatPhone(r.phone ?? null), r.email, r.mayContact === false && 'do not contact')) : [];
    }
    case 'education': {
      const parsed = schemas.education.safeParse(value);
      return parsed.success ? parsed.data.map((e) => joined(e.institution, e.degreeEarned, e.location, range(e.fromDate, e.toDate))) : [];
    }
    case 'credentials': {
      const parsed = schemas.credentials.safeParse(value);
      return parsed.success ? parsed.data.map((c) => joined(c.credentialType, c.credentialNumber && `no. ${c.credentialNumber}`, c.issuingState, c.expirationDate && `expires ${c.expirationDate}`)) : [];
    }
    case 'contact.phone':
    case 'contact.alternatePhone': {
      const parsed = schemas[field].safeParse(value);
      return parsed.success ? [formatPhone(parsed.data)] : [];
    }
    default: {
      const parsed = schemas[field].safeParse(value);
      return parsed.success ? [String(parsed.data)] : [];
    }
  }
}

/** The four fields that live only on the application, in the order the card lists them. */
export const APPLICATION_FIELDS = [
  ['employmentHistory', 'Employment history'],
  ['references', 'References'],
  ['education', 'Education'],
  ['credentials', 'Licences and credentials, as typed'],
] as const satisfies ReadonlyArray<readonly [ProfileField, string]>;

